import React from 'react';
import { ArrowLeft, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Link } from 'react-router-dom';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-rose-100">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-rose-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link to="/" className="inline-flex items-center text-gray-600 hover:text-rose-600 mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-rose-400 to-rose-600 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Terms of Service</h1>
              <p className="text-sm text-gray-600">Agreement for using Constant Love</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        <Card className="p-6 sm:p-8 bg-white/80 backdrop-blur-sm border-rose-200">
          <div className="prose prose-gray max-w-none">
            <p className="text-gray-600 mb-8">
              <strong>Last Updated:</strong> {new Date().toLocaleDateString()}<br />
              <strong>Effective Date:</strong> September 20, 2025
            </p>

            <div className="bg-rose-50 border border-rose-200 rounded-lg p-6 mb-8">
              <h3 className="text-lg font-semibold text-rose-900 mb-3">
                🤖 Service Description
              </h3>
              <p className="text-rose-800">
                <strong>Constant Love is a self-love support tool, not a medical or therapy service.</strong> 
                Our AI companion Conchi provides AI-powered emotional support, affirmations, and wellness features. 
                Constant Love cannot replace professional mental health treatment.
              </p>
            </div>

            <h2 className="text-xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="mb-6">
              By accessing or using Constant Love provided by Constant Love Co, LLC ("we," "us," "our," or "Constant Love"), 
              you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, 
              please do not use our service.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mb-4">2. Eligibility</h2>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li><strong>Age Requirement:</strong> You must be 18 years or older to create an account and use this service</li>
              <li><strong>Parental Consent:</strong> Users under 18 must have explicit parental or guardian consent</li>
              <li><strong>Legal Capacity:</strong> You must have the legal capacity to enter into a binding agreement</li>
              <li><strong>Account Responsibility:</strong> You are responsible for maintaining account security and all activities under your account</li>
              <li><strong>Accurate Information:</strong> You agree to provide accurate and current information during registration</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-900 mb-4">3. Service Description</h2>
            
            <h3 className="text-lg font-semibold text-gray-900 mb-3">What Constant Love Provides:</h3>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Conchi - AI-powered self-love companion and emotional support</li>
              <li>Personalized daily affirmations based on mood and preferences</li>
              <li>AI-powered journaling with insights and pattern recognition</li>
              <li>Divine Guidance - personalized astrology readings and insights</li>
              <li>Love Garden - match-3 puzzle game for relaxation and Love Score earning</li>
              <li>Vision Board - digital goal visualization and manifestation tool</li>
              <li>Love Vault - personal collection of saved affirmations and guidance</li>
              <li>Community features for connection and peer support</li>
              <li>Love Score system with gamified progress tracking</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">What Constant Love Does NOT Provide:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Medical advice, diagnosis, or treatment</li>
              <li>Professional therapy, counseling, or psychiatric services</li>
              <li>Crisis intervention or emergency mental health support</li>
              <li>Guarantee of specific emotional, mental health, or life outcomes</li>
              <li>Human therapist or counselor consultations</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-900 mb-4">4. Subscription Plans & Billing</h2>
            
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Available Plans:</h3>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="font-semibold text-blue-900 mb-2">Reflect (Free) Plan:</p>
              <ul className="text-blue-800 space-y-1 text-sm">
                <li>• 1 daily affirmation</li>
                <li>• 5 Conchi messages per day</li>
                <li>• 1 AI journal insight per week</li>
                <li>• 1 Divine Guidance reading per week</li>
                <li>• 10 Love Vault saves per month</li>
                <li>• 1 Vision Board maximum</li>
                <li>• Full Love Garden game and Community access</li>
              </ul>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <p className="font-semibold text-green-900 mb-2">Growth Plan - $17/month:</p>
              <ul className="text-green-800 space-y-1 text-sm">
                <li>• UNLIMITED access to all features</li>
                <li>• Unlimited Conchi conversations</li>
                <li>• Unlimited AI journal insights</li>
                <li>• Unlimited Divine Guidance readings</li>
                <li>• Unlimited Love Vault saves</li>
                <li>• Unlimited Vision Boards</li>
                <li>• Priority customer support</li>
              </ul>
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">Free Trial:</h3>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>All new users receive a 7-day free trial of the Growth plan</li>
              <li>No credit card required to start trial</li>
              <li>After trial ends, automatically downgrade to Reflect (free) plan</li>
              <li>Upgrade to Growth plan anytime during or after trial</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">Billing Terms:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li><strong>Auto-Renewal:</strong> Growth plan subscriptions automatically renew monthly unless cancelled</li>
              <li><strong>Payment Processing:</strong> All payments securely handled by Stripe</li>
              <li><strong>Cancellation:</strong> Cancel anytime with no penalties or cancellation fees</li>
              <li><strong>Access After Cancellation:</strong> Retain Growth plan access until current billing period ends</li>
              <li><strong>Refunds:</strong> Refunds considered on case-by-case basis (see Section 10)</li>
              <li><strong>Price Changes:</strong> 30-day advance notice for price increases to existing subscribers</li>
              <li><strong>Failed Payments:</strong> Service may be suspended if payment fails; account reactivated upon successful payment</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-900 mb-4">5. User Content & Ownership</h2>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Your Content:</h3>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>You Own It:</strong> You retain all rights to your journal entries, conversations, affirmations, and personal data</li>
              <li><strong>License to Us:</strong> You grant Constant Love Co, LLC a limited license to use your content to provide and improve the service (e.g., for Conchi to reference your previous conversations)</li>
              <li><strong>Privacy:</strong> Your private content (journals, Conchi chats) is not shared publicly unless you explicitly choose to share</li>
              <li><strong>Community Content:</strong> Content you post in community channels becomes visible to other users and may be shared</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">Our Content:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Constant Love Co, LLC owns all intellectual property rights to the Constant Love software, AI models, features, and branding</li>
              <li>You may not copy, modify, distribute, or reverse-engineer our service</li>
              <li>Affirmations and guidance generated by AI are part of the service but personalized to you</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-900 mb-4">6. Prohibited Uses</h2>
            <p className="mb-4">You agree NOT to use Constant Love for:</p>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Posting harmful, abusive, harassing, or offensive content</li>
              <li>Attempting to hack, exploit, or compromise the security of the service</li>
              <li>Reverse-engineering or extracting AI models or algorithms</li>
              <li>Sharing false, misleading, or deceptive information</li>
              <li>Violating others' privacy, intellectual property, or legal rights</li>
              <li>Commercial use or reselling of the service without authorization</li>
              <li>Spamming, phishing, or distributing malware</li>
              <li>Impersonating others or creating fake accounts</li>
              <li>Scraping or automated data collection from the service</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-900 mb-4">7. Community Guidelines</h2>
            <p className="mb-4">When using community features, you must:</p>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Be respectful, kind, and supportive to all members</li>
              <li>Respect others' privacy and confidentiality</li>
              <li>Avoid triggering content without appropriate warnings</li>
              <li>Report inappropriate behavior or content</li>
              <li>Follow our full Community Guidelines (available in the app)</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-900 mb-4">8. Limitation of Liability</h2>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-yellow-900 mb-3">⚠️ Critical Disclaimers:</h3>
              <ul className="space-y-2 text-yellow-800">
                <li>• <strong>AI Limitations:</strong> AI responses may be inaccurate, inappropriate, or unhelpful. Use your judgment.</li>
                <li>• <strong>No Medical Liability:</strong> We are not responsible for emotional, mental health, medical, or life outcomes resulting from service use.</li>
                <li>• <strong>User Responsibility:</strong> You are solely responsible for your decisions and actions based on Conchi's suggestions or any service content.</li>
                <li>• <strong>"As-Is" Service:</strong> Constant Love is provided "as-is" without warranties of any kind.</li>
                <li>• <strong>Service Availability:</strong> We strive for reliability but cannot guarantee uninterrupted, error-free service.</li>
                <li>• <strong>Maximum Liability:</strong> Our liability is limited to the amount you paid for the service in the last 12 months (or $100, whichever is greater).</li>
                <li>• <strong>No Consequential Damages:</strong> We are not liable for indirect, incidental, special, or consequential damages.</li>
              </ul>
            </div>

            <p className="mb-6">
              <strong>Important:</strong> Some jurisdictions do not allow limitations of liability for consequential damages. 
              In such jurisdictions, our liability is limited to the fullest extent permitted by law.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mb-4">9. Indemnification</h2>
            <p className="mb-6">
              You agree to indemnify, defend, and hold harmless Constant Love Co, LLC, its officers, directors, employees, 
              and partners from any claims, damages, losses, or expenses (including legal fees) arising from:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Your use or misuse of the service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any rights of another person or entity</li>
              <li>Content you submit or share through the service</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-900 mb-4">10. Refund Policy</h2>
            <p className="mb-4">
              We want you to be satisfied with Constant Love. Refund requests are considered on a case-by-case basis within 
              7 days of purchase if:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Technical issues prevented you from using the service</li>
              <li>Billing error or duplicate charge occurred</li>
              <li>Service did not function as described</li>
            </ul>
            <p className="mb-6">
              To request a refund, email support@constantloveco.com with your account details and reason. 
              Refunds are not available for subscription renewals if service was used during that period.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mb-4">11. Account Termination</h2>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Your Right to Terminate:</h3>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>You may close your account anytime through Profile Settings</li>
              <li>Cancel Growth subscription anytime without penalty</li>
              <li>Download your data before closing account (cannot be recovered after)</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">Our Right to Terminate:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>We may suspend or terminate accounts for Terms violations</li>
              <li>We may terminate for fraudulent activity or abuse</li>
              <li>We may discontinue the service entirely with 30 days notice</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">Effect of Termination:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Account data deleted within 30 days of termination</li>
              <li>Outstanding charges remain due</li>
              <li>No refunds for unused subscription time (except as stated in Refund Policy)</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-900 mb-4">12. Changes to Terms</h2>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>We may update these Terms as our service evolves or laws change</li>
              <li>Significant changes will be communicated via email or in-app notification</li>
              <li>Continued use after changes constitutes acceptance of updated Terms</li>
              <li>You can always review current Terms in the app or at constantloveco.com</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-900 mb-4">13. Dispute Resolution & Arbitration</h2>
            <p className="mb-4">
              <strong>Informal Resolution:</strong> Before formal proceedings, please contact us at legal@constantloveco.com 
              to resolve disputes informally.
            </p>
            <p className="mb-4">
              <strong>Binding Arbitration:</strong> If informal resolution fails, disputes will be resolved through binding 
              arbitration under the American Arbitration Association (AAA) rules, rather than in court.
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Arbitration is conducted remotely or in your county of residence</li>
              <li>Each party pays their own costs unless arbitrator decides otherwise</li>
              <li>Class action lawsuits are waived</li>
              <li>Small claims court remains available for qualifying disputes</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-900 mb-4">14. Governing Law</h2>
            <p className="mb-6">
              These Terms are governed by the laws of the State of Delaware and federal laws of the United States, 
              without regard to conflict of law principles. Any disputes not subject to arbitration will be resolved 
              in the courts of Delaware.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mb-4">15. Miscellaneous</h2>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li><strong>Entire Agreement:</strong> These Terms constitute the entire agreement between you and Constant Love Co, LLC</li>
              <li><strong>Severability:</strong> If any provision is found invalid, the rest remains enforceable</li>
              <li><strong>No Waiver:</strong> Our failure to enforce any right doesn't waive that right</li>
              <li><strong>Assignment:</strong> We may assign these Terms; you may not without our consent</li>
              <li><strong>Force Majeure:</strong> We're not liable for delays due to circumstances beyond our control</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-900 mb-4">16. Contact Information</h2>
            <p className="mb-4">
              Questions about these Terms? Contact us:
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-8">
              <p><strong>Constant Love Co, LLC</strong></p>
              <p className="mt-2">Legal Inquiries: legal@constantloveco.com</p>
              <p>General Support: support@constantloveco.com</p>
              <p>Help Center: Available in the Constant Love app</p>
              <p className="mt-2">Response time: Within 48 hours</p>
            </div>

            <div className="border-t pt-6 mt-8">
              <p className="text-sm text-gray-600">
                By creating an account and using Constant Love, you acknowledge that you have read, understood, 
                and agree to be bound by these Terms of Service. These Terms protect both you and Constant Love Co, LLC 
                as we work together on your self-love journey.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default TermsOfService;